<script lang="ts" setup>
import { Carousel, Slide, Pagination, Navigation } from 'vue3-carousel'
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useI18n } from 'vue-i18n';

const { locale } = useI18n()
const props = defineProps({
    product:{
        type: Object,
        required: true
    }
})
const galleryFullscreen = ref(false)
const currentSlide = ref(0)

const imageUrls = computed(() => {
    return props.product.images.map(img => ({
        id: img.id,
        thumbnail: `https://assets.zokkus.com/images/format-250x250/${img.name}`,
        full: `https://assets.zokkus.com/images/format-${galleryFullscreen.value ? '2048x1536' : '650x350'}/${img.name}`
    }));
});

const carouselConfig={
    itemsToShow: 1,
    wrapAround: true,
    slideEffect: 'fade',
    mouseDrag: false,
    touchDrag: true,
}
const thumbnailsConfig = computed(()=>{
    return {
        height: 80,
        itemsToShow: props.product.images.length>5 ? 6: props.product.images.length,
        wrapAround: true,
        touchDrag: false,
        gap: 10,
    };
});

function leaveFullScreen(event: MouseEvent){
    event.stopPropagation();
    
    galleryFullscreen.value = false
}
function handleKeydown(event) {
    if (event.key === 'Escape' && galleryFullscreen.value) {
        galleryFullscreen.value = false;
    }
}
const slideToSlide = (nextSlide: number) => {
    currentSlide.value = nextSlide
}
onMounted(() => {
    window.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeydown);
});

</script>
<template>
    <div class="prod_gallery" :class="{ 'fullscreen': galleryFullscreen }">
        <Carousel id="gallery" v-bind="carouselConfig" v-model="currentSlide">
            <template v-if="imageUrls.length>0">
                <Slide v-for="img in imageUrls" :key="img.id" @click="galleryFullscreen = !galleryFullscreen">
                    <img :src="img.full" :alt="props.product.content[locale].name" />
                </Slide>
            </template> 
            <Slide v-else>
                <img src="/img/no-picture.jpg" alt="No picture" />
            </Slide>
            <template #addons>
                <v-btn
                    v-if="galleryFullscreen"
                    class="close-gallery"
                    color="black"
                    icon="mdi-close"
                    @click="leaveFullScreen"
                ></v-btn>
                <img class="gallery_logo" src="@/assets/logos/kase-logo-alone.png" alt="Kase Filters" />
                <div class="gallery_title">{{ props.product.content[locale].name }}</div>
                <Navigation class="gallery_navigation" />
                <!--<Pagination class="gallery_pagination" />-->
            </template>
        </Carousel>
        <Carousel v-if="imageUrls.length>0" id="thumbnails" v-bind="thumbnailsConfig" v-model="currentSlide">
            <Slide v-for="img in imageUrls" :key="img.id">
                <template #default="{ currentIndex, isActive }">
                    <div
                    :class="['thumbnail', { 'is-active': isActive }]"
                    @click="slideToSlide(currentIndex)"
                    >
                        <img :src="img.thumbnail" :alt="props.product.content[locale].name" class="thumbnail-image" />
                    </div>
                </template>
            </Slide>
            <template #addons>
                <!--<Navigation />-->
            </template>
        </Carousel>
    </div>
</template>
<style>
:root {
    --vc-clr-primary: #C7C7C7;
    --vc-clr-secondary: #DC0303;
    --vc-nav-height: 50px;
    --vc-nav-width: 50px;
}
</style>
<style lang="scss" scoped>
#thumbnails{
    img{
        object-fit: contain;
        height: 80px;
        width: 100%;
    }
}
.gallery_title, .gallery_logo{
    display: none;
}
.prod_gallery{
    width: 100%;
    display: flex;
    position: relative;
    flex-direction: column;
    transition: all 0.3s;
    background-color: #FFF;
    height: 257px;
    #gallery{
        width: 100%;
        height: 100%;
        img{
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    }
    #thumbnails{
        display: none;
        width: 100%;
        max-width: 100vw;
        img{
            width: 100%;
            height: 80px;
            object-fit: contain;
        }
    }
    .thumbnail{
        width: auto;
        height: 100px;
        cursor: pointer;
        img{
            width: 100%;
            height: 100%;
            object-fit: contain;
            opacity: 0.5;
            transition: all 0.3s;
        }
        &.is-active, &:hover{
            img{
                opacity: 1;
            }
        }
    }
    &.fullscreen{
        .close-gallery{
            position: fixed;
            top: 10px;
            right: 10px;
            z-index: 2002;
        }
        .gallery_title{
            display: block;
            position: fixed;
            top: 10px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 2001;
            background-color: #FFF;
            padding: 10px;
            border-radius: 5px;
            box-shadow: 0px 0px 10px 0px #00000040;
        }
        img.gallery_logo{
            display: block;
            position: fixed;
            top: 10px;
            left: 10px;
            z-index: 2001;
            width: 100px !important;
            height: 50px !important;
            object-fit: contain;
            
        }
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 2000;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        #gallery{
            &::deep(.gallery_pagination){
                height: calc(100vh - 100px);
                max-width: 100vw;
            }
            img{
                width: 100%;
                height: 100%;
                object-fit: contain;
            }
            &:deep(.gallery_pagination){
                display: none;
            }
        }
        #thumbnails{
            display: block;
            width: 100%;
            height: 100px;
            max-width: 100vw;
            img{
                width: 100%;
                height: 100px;
                object-fit: contain;
            }
        }
    }
}
@media only screen and (min-width : $responsive-point) {
    .prod_gallery{
        #gallery{
            height: calc(100% - 100px);
            &:deep(.gallery_navigation){
                display: none;
            }
            &:deep(.gallery_pagination){
                display: none;
            }
        }
        #thumbnails{
            display: block;
            height: 100px;
        }
    }
}
</style>